/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Flex, Button, HStack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useForm} from 'react-hook-form'
import ZipCodeFields from '../../../components/forms/zip-code-fields'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import InfoPopover from '../../popovers/info-popover'
import {InfoIcon, EditIcon} from '../../icons'

export const useZipCode = () => {
    const {site, locale} = useMultiSite()
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()
    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency
    const updateBasket = useShopperBasketsMutation('updateBasket')

    const submitZipCode = async ({code}) => {
        try {
            const response = await updateBasket.mutateAsync({
                parameters: {basketId: basket.basketId},
                body: {currency, c_zipCodePreview: code},
            })
            form.reset({code: code})
            if (!response || !response?.c_taxPreviewAmount) {
                form.setError(
                    'code',
                    {
                        type: 'manual',
                        message: formatMessage({
                            defaultMessage: 'Check the code and try again.',
                            id: 'use_zipcode.error.check_the_code',
                        }),
                    },
                    {shouldFocus: true},
                )
                return false
            }

            return true
        } catch (e) {
            form.setError(
                'code',
                {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage: 'Check the code and try again.',
                        id: 'use_zipcode.error.check_the_code',
                    }),
                },
                {shouldFocus: true},
            )
            return false
        }
    }

    const removeZipCode = async () => {
        try {
            await updateBasket.mutateAsync({
                parameters: {basketId: basket?.basketId},
                body: {currency, c_taxPreviewAmount: null},
            })
        } catch (e) {
            form.setError(
                'code',
                {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage: 'Check the code and try again.',
                        id: 'use_zipcode.error.check_the_code',
                    }),
                },
                {shouldFocus: true},
            )
        }
    }

    return {form, submitZipCode, removeZipCode}
}

export const ZipCode = ({form, submitZipCode, removeZipCode, basket, step}) => {
    const style = useMultiStyleConfig('OrderSummary', {})
    const {formatMessage} = useIntl()
    const {zipCode} = style
    const [showInput, setShowInput] = useState(true)
    const checkoutStep = step || null
    const submitInput = async (data) => {
        const success = await submitZipCode(data)
        setShowInput(success ? false : true)
    }

    const removeButton = async (data) => {
        await removeZipCode(data)
        setShowInput(true)
    }
    return (
        <HStack
            {...zipCode?.zipCodeContainer}
            data-testid="zip-code-form"
            as="form"
            gap={'xsmall.base'}
            onSubmit={form.handleSubmit(submitInput)}
        >
            <Flex {...zipCode?.zipCodeInfoContainer}>
                <Text {...zipCode?.zipCodeText}>
                    <FormattedMessage
                        id="use_zip_code_fields.label.zip_code"
                        defaultMessage="Tax"
                    />
                </Text>
                <InfoPopover
                    variant="top-right"
                    trigger={
                        <InfoIcon
                            {...style?.subtotalSection?.infoIcon}
                            aria-hidden="true"
                            tabIndex={0}
                            focusable="true"
                        />
                    }
                    openTooltipOn="hover"
                    title={formatMessage({
                        id: 'order_summary.taxPreview.info.title',
                        defaultMessage: 'Tax Preview Information',
                    })}
                    description={formatMessage({
                        id: 'order_summary.taxPreview.info.description',
                        defaultMessage: 'Tax estimation based on zip code.',
                    })}
                />
            </Flex>

            {showInput && !(checkoutStep === 3 || checkoutStep === 4) ? (
                <ZipCodeFields form={form} />
            ) : (
                <Flex
                    {...zipCode?.zipCodeEditContainer}
                    as="form"
                    onSubmit={form.handleSubmit(removeZipCode)}
                >
                    {!checkoutStep || checkoutStep === 1 || checkoutStep === 2 ? (
                        <>
                            <Text {...zipCode?.zipCodeText}>{basket?.c_taxPreviewAmount}</Text>
                            <Button
                                {...zipCode?.zipCodeEdit}
                                isLoading={form.formState.isSubmitting}
                                onClick={() => removeButton()}
                            >
                                <EditIcon {...zipCode?.zipCodeEditIcon} />
                            </Button>
                        </>
                    ) : (
                        <Text>{basket?.c_avalaraTaxAmount}</Text>
                    )}
                </Flex>
            )}
        </HStack>
    )
}

ZipCode.propTypes = {
    /** The form object returned from `usePromoCode` hook */
    form: PropTypes.object.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    submitZipCode: PropTypes.func.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    removeZipCode: PropTypes.func.isRequired,
    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object,

    promoStyle: PropTypes.object,

    /** The current basket object */
    basket: PropTypes.object,

    step: PropTypes.any,
}

// Ensure that the parent component passes the required props, including removeZipCode
export default function ZipCodeWrapper(props) {
    const {form, submitZipCode, removeZipCode} = useZipCode()
    const {basket} = useCurrentBasket()

    return (
        <ZipCode
            form={form}
            submitZipCode={submitZipCode}
            removeZipCode={removeZipCode}
            basket={basket}
            {...props}
        />
    )
}
